import React, { useRef } from "react";
import styled from "styled-components";
import MarcButton from "@/components/buttons/MarcButton";
import { useNavigate } from "react-router-dom";
import border from "@/assets/images/blank_zelda_border.png";
import ScrollMoreIndicator from "@/components/ScrollMoreIndicator";
import electronics from "@/assets/images/electronics_pixel.png";
import paintbrush from "@/assets/images/paintbrush_pixel2.png";
import soccer from "@/assets/images/pixel_nes_football_np.png";
import { Heading2, Paragraph } from "@/components/typography";
import { Spacer } from "@/features/Resume";
import ai from "@/assets/images/ai_pixel.png";
import controller from "@/assets/images/nes_controller_pixel.png";
import printer from "@/assets/images/printer_pixel.png";
import saw from "@/assets/images/saw_pixel.png";
import tomes from "@/assets/images/pixel_tomes.png";
import interestsData from "@/data/interests.json";

const imageMap: Record<string, string> = {
  ai,
  electronics,
  controller,
  printer,
  saw,
  paintbrush,
  tomes,
  soccer,
};

/* ── Layout containers ─────────────────────────────────────────────── */

const PageContainer = styled.div`
  background: black;
  color: white;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  gap: 24px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1199px) {
    flex-direction: column;
    gap: 12px;
    padding: 48px 16px 16px;
  }
`;

const BackColumn = styled.div`
  position: absolute;
  top: 40px;
  left: 20px;
  z-index: 10;

  @media (max-width: 1199px) {
    top: 12px;
    left: 12px;
  }
`;

/* ── Scroll frame (Zelda-bordered window) ──────────────────────────── */

const ScrollFrame = styled.div`
  flex-shrink: 0;
  border-image: url(${border}) 8 / 32px / 0 round;
  border-width: 32px;
  border-style: inset;
  width: 600px;
  height: 80vh;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1199px) {
    width: min(600px, calc(100vw - 32px));
    flex: 1;
    min-height: 0;
    height: auto;
  }

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
  }
`;

const ScrollFrameWrapper = styled.div`
  position: relative;
  flex-shrink: 0;

  @media (max-width: 1199px) {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const ScrollContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

/* ── Types ─────────────────────────────────────────────────────────── */

interface Interest {
  id: string;
  title: string;
  imageKey: string;
  imageHeight: number;
  imageWidth?: number;
  description: string;
}

/* ── Component ─────────────────────────────────────────────────────── */

function Interests() {
  const navigate = useNavigate();
  const { interests } = interestsData as { interests: Interest[] };
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <PageContainer>
      <BackColumn>
        <MarcButton onClick={() => navigate("/")} label={"Back"} />
      </BackColumn>

      <ScrollFrameWrapper>
        <ScrollFrame ref={scrollRef}>
          <ScrollContent>
            {interests.map((interest, index) => (
              <React.Fragment key={interest.id}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={imageMap[interest.imageKey]}
                    height={interest.imageHeight}
                    width={interest.imageWidth}
                    alt={`${interest.title} pixel art`}
                  />
                  <Heading2
                    style={{ textAlign: "center", paddingLeft: 24 }}
                  >
                    {interest.title}
                  </Heading2>
                </div>
                <Paragraph
                  style={{ paddingTop: 36, fontSize: 20, lineHeight: "28px" }}
                >
                  {interest.description}
                </Paragraph>
                {index < interests.length - 1 && <Spacer />}
              </React.Fragment>
            ))}
          </ScrollContent>
        </ScrollFrame>
        <ScrollMoreIndicator scrollRef={scrollRef} />
      </ScrollFrameWrapper>
    </PageContainer>
  );
}

export default Interests;
